import { T, badgeColors, fieldInput, fmtDate, S } from "./theme";

/* ── Badge ── */
export const Badge = ({ variant = "dim", children }) => (
  <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: badgeColors[variant].bg, color: badgeColors[variant].color }}>
    {children}
  </span>
);

/* ── Button ── */
export const Btn = ({ variant = "ghost", sm, children, style: sx, ...rest }) => {
  const base = { display: "inline-flex", alignItems: "center", gap: 6, padding: sm ? "5px 10px" : "8px 16px", borderRadius: T.radiusSm, fontFamily: T.font, fontSize: sm ? 12 : 13, fontWeight: 500, cursor: "pointer", border: "none", transition: "all 0.15s", ...sx };
  const variants = {
    primary: { background: T.accent, color: "#fff" },
    ghost: { background: "transparent", color: T.textMuted, border: `1px solid ${T.border}` },
    green: { background: T.greenSoft, color: T.green, border: "1px solid rgba(10,160,110,0.15)" },
    red: { background: T.redSoft, color: T.red, border: "1px solid rgba(220,69,69,0.15)" },
  };
  return <button style={{ ...base, ...variants[variant] }} {...rest}>{children}</button>;
};

/* ── CardExpandRail — full-height strip on a card's right edge. Click toggles
     expand/collapse (chevron rotates); the card body decides what to render
     in each state. Kept dumb on purpose — the options list can grow to any
     size later without this component changing. ── */
export const CardExpandRail = ({ expanded, onToggle, title }) => (
  <button
    onClick={onToggle}
    title={title || (expanded ? "Collapse" : "Show breakdown")}
    style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      width: 30, alignSelf: "stretch", background: T.surface2, border: "none",
      borderLeft: `1px solid ${T.border}`, cursor: "pointer", color: T.textMuted,
      transition: "background 0.15s, color 0.15s",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = T.accentSoft; e.currentTarget.style.color = T.accent; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = T.surface2; e.currentTarget.style.color = T.textMuted; }}
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: expanded ? "rotate(90deg)" : "rotate(-90deg)", transition: "transform 0.15s" }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </button>
);

/* ── CardMetricList — expanded state for the Overview cards: one row per
     metric (label + value). Add more entries to `metrics` any time, no
     layout changes needed. ── */
export const CardMetricList = ({ metrics }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "26px 22px" }}>
    {metrics.map((m) => (
      <div key={m.label} title={m.title} style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6 }}>{m.label}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.accent, lineHeight: 1 }}>{typeof m.value === "number" ? m.value.toLocaleString() : m.value}</div>
      </div>
    ))}
  </div>
);

/* ── Toast ── */
export const Toast = ({ message, visible }) => (
  <div style={{ position: "fixed", bottom: 24, right: 24, background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "12px 20px", fontSize: 13, boxShadow: T.shadow, zIndex: 200, transform: visible ? "translateY(0)" : "translateY(80px)", opacity: visible ? 1 : 0, transition: "all 0.3s", color: T.text }}>
    {message}
  </div>
);

/* ── Modal (centered, for Add forms) ── */
export const Modal = ({ open, title, fields, onClose, onSave }) => (
  <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, opacity: open ? 1 : 0, pointerEvents: open ? "all" : "none", transition: "opacity 0.2s" }}>
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 28, width: 480, maxWidth: "90vw", boxShadow: T.shadow, transform: open ? "translateY(0)" : "translateY(8px)", transition: "transform 0.2s" }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>{title}</h3>
      {fields?.map((f) => (
        <div key={f.label} style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: T.textMuted, marginBottom: 5 }}>{f.label}</label>
          <input type={f.type || "text"} placeholder={f.placeholder || ""} defaultValue={f.value || ""} disabled={f.disabled} style={{ ...fieldInput, opacity: f.disabled ? 0.5 : 1 }} />
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={onSave}>Save</Btn>
      </div>
    </div>
  </div>
);

/* ── Drawer (right-side slide) ── */
export const Drawer = ({ open, title, footer, children, onClose }) => (
  <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 100, opacity: open ? 1 : 0, pointerEvents: open ? "all" : "none", transition: "opacity 0.25s" }}>
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 420, maxWidth: "90vw", background: T.surface, borderLeft: `1px solid ${T.border}`, boxShadow: "-8px 0 32px rgba(0,0,0,0.1)", transform: open ? "translateX(0)" : "translateX(100%)", transition: "transform 0.3s cubic-bezier(0.16,1,0.3,1)", zIndex: 101, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600 }}>{title}</h3>
        <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surface2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted, fontSize: 16 }}>✕</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>{children}</div>
      <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8, justifyContent: "flex-end", flexShrink: 0 }}>{footer}</div>
    </div>
  </div>
);

/* ── Drawer helpers ── */
export const DrawerMeta = ({ items }) => (
  <div style={{ display: "flex", gap: 16, padding: "12px 16px", background: T.surface2, borderRadius: T.radiusSm, marginBottom: 20 }}>
    {items.map(({ label, value, mono }) => (
      <div key={label} style={{ flex: 1 }}>
        <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text, fontFamily: mono ? T.mono : T.font }}>{value}</div>
      </div>
    ))}
  </div>
);

export const DrawerSection = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.8, margin: "20px 0 10px" }}>{children}</div>
);

export const DrawerField = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: T.textMuted, marginBottom: 5 }}>{label}</label>
    {children}
  </div>
);

/* ── Provider Drawer Content (edit mode) ── */
export const ProviderDrawerContent = ({ provider: p, specialties = [] }) => (
  <>
    <DrawerMeta items={[{ label: "SECID", value: p.secid, mono: true }, { label: "Station", value: `${p.station_id} — ${p.site_name || ""}` }, { label: "VISN", value: p.visn }]} />
    <DrawerSection>Provider Information</DrawerSection>
    <DrawerField label="SECID"><input style={{ ...fieldInput, opacity: 0.5 }} value={p.secid} disabled /></DrawerField>
    <DrawerField label="Provider Name"><input style={fieldInput} defaultValue={p.provider_name} /></DrawerField>
    <DrawerField label="Email"><input style={fieldInput} type="email" defaultValue={p.email} /></DrawerField>
    <DrawerSection>Assignment</DrawerSection>
    <DrawerField label="Station ID"><input style={fieldInput} defaultValue={p.station_id} /></DrawerField>
    <DrawerField label="Specialty">
      <select style={{ ...fieldInput, appearance: "auto", cursor: "pointer" }} defaultValue={p.specialty}>
        {specialties.map((s) => <option key={s}>{s}</option>)}
      </select>
    </DrawerField>
    <DrawerSection>Activation</DrawerSection>
    <DrawerField label="Date Activated"><input style={fieldInput} type="date" defaultValue={p.date_activated} /></DrawerField>
  </>
);

/* ── Station Drawer Content (view + edit mode) ── */
export const StationDrawerContent = ({ station: s, editable, recentProviders = [] }) => {
  const licensed = s.licensed_to_date ?? s.activated_to_date ?? s.activated ?? 0;
  const activated = s.activated_to_date ?? s.activated ?? 0;
  return (
    <>
      <DrawerMeta items={[{ label: "Station ID", value: s.station_id, mono: true }, { label: "VISN", value: s.visn }, { label: "Total Licenses", value: licensed }, { label: "Launched Licenses", value: activated }]} />
      <DrawerSection>Utilization Summary</DrawerSection>
      <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 24 }}>
        <div style={{ background: T.surface2, borderRadius: T.radiusSm, padding: 14, textAlign: "center", minWidth: 120 }}>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: T.mono, color: T.text }}>{licensed}</div>
          <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>Total Licenses</div>
        </div>
        <div style={{ background: T.greenSoft, borderRadius: T.radiusSm, padding: 14, textAlign: "center", minWidth: 120 }}>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: T.mono, color: T.green }}>{activated}</div>
          <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>Launched Licenses</div>
        </div>
      </div>
      {editable ? (
        <>
          <DrawerSection>Station Details</DrawerSection>
          <DrawerField label="Station ID"><input style={{ ...fieldInput, opacity: 0.5 }} value={s.station_id} disabled /></DrawerField>
          <DrawerField label="VISN"><input style={fieldInput} type="number" defaultValue={s.visn} /></DrawerField>
          <DrawerField label="Site Name"><input style={fieldInput} defaultValue={s.site_name} /></DrawerField>
          <DrawerField label="City"><input style={fieldInput} defaultValue={s.city} /></DrawerField>
        </>
      ) : (
        <>
          <DrawerSection>Recent Activations</DrawerSection>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr>{["Provider", "Specialty", "Activated"].map((h) => <th key={h} style={{ ...S.th, fontSize: 10 }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {recentProviders.slice(0, 3).map((p) => (
                <tr key={p.secid}>
                  <td style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}` }}>{p.provider_name}</td>
                  <td style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}` }}><Badge>{p.specialty}</Badge></td>
                  <td style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}` }}>{fmtDate(p.date_activated)}</td>
                </tr>
              ))}
              {recentProviders.length === 0 && (
                <tr><td colSpan={3} style={{ padding: "12px 10px", color: T.textDim, fontSize: 12 }}>No recent activations</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};
